import React, { useEffect, useMemo, useState } from 'react';
import { ToothData } from './teethData';
import DENTToothSurfaces from './DENTToothSurfaces';
import { ToothView } from '../../Enums/ToothView.enum';
import { Scale } from '../../../Base/Interfaces/Scale.interface';
import styles from './DENTToothItem.module.scss';
import { joinClasses } from '../../../../Utils/className.util';
import { useDENTContext } from '../../Services/DENTContext';
import { OverviewMode } from '../../Enums/OverviewMode.enum';
import DENTToothQuickSelected from './DENTToothQuickSelected';
import { Tooth, ToothState } from '../../../../graphql/graphql';
import useTranslation from '../../../../Hooks/useTranslation';
import DENTToothVisual from '../ToothVisual/DENTToothVisual';
import DENTToothGrid from './DENTToothGrid';
import DENTToothIcons from './DENTToothIcons';
import DENTToothPeriodontalLine from '../DENTToothPeriodontalLine/DENTToothPeriodontalLine';

interface Props {
	data: Tooth;
	view: ToothView;
	scale: Scale;
	isDetail: boolean;
	showToothGrid?: boolean;
}

const tootImageNamePerView: Record<ToothView, string> = {
	[ToothView.INCISAL]: 'incisal.png',
	[ToothView.PALATAL]: 'palatal.png',
	[ToothView.LINGUAL]: 'lingual.png',
	[ToothView.BUCCAL]: 'buccal.png',
};

const getImageFolder = (tooth: Tooth) => {
	switch (tooth.state) {
		case ToothState.Crown:
			return 'tooth-crown';
		case ToothState.Missing:
			return 'missing-tooth';
		case ToothState.Implant:
			return 'tooth-implant';
		default:
			return 'tooth';
	}
};

const DENTToothItem: React.FC<Props> = ({
	data,
	view,
	scale,
	isDetail,
	showToothGrid,
}) => {
	const {
		isToothQuickSelected,
		quickSelectMode,
		activeTooth,
		showPeriodontal,
		showDental,
		toothMap,
		overviewMode,
	} = useDENTContext();
	const { t } = useTranslation();

	const tooth = useMemo(
		() => (activeTooth?.number === data.number ? activeTooth : data),
		[activeTooth, data],
	);

	const toothData = ToothData[tooth.number!];
	const tData = toothData?.[view];
	const image = `https://cdn.meddi.com/DENT/${getImageFolder(tooth)}/${
		tooth.number
	}/${tootImageNamePerView[view]}`;
	const [originalSize, setOriginalSize] = useState<{
		w: number;
		h: number;
	} | null>(null);
	const [renderSize, setRenderSize] = useState<{ w: number; h: number } | null>(
		null,
	);

	useEffect(() => {
		// @ts-ignore
		const img = new Image();
		img.src = image;
		img.onload = () => {
			setOriginalSize({ w: img.naturalWidth, h: img.naturalHeight });
		};
	}, [image]);

	useEffect(() => {
		if (!originalSize) return;
		const renderScale = Math.max(scale.horizontal, scale.vertical);
		const render = {
			w: originalSize.w / renderScale,
			h: originalSize.h / renderScale,
		};
		setRenderSize(render);
	}, [originalSize, scale]);

	if (!tData || !originalSize || !renderSize) {
		return null;
	}

	const isToothDetailView = overviewMode === OverviewMode.DEFAULT;
	const showExtractedWrapper =
		data.state === ToothState.ToBeExtracted &&
		(!activeTooth || !isToothDetailView);
	const showExtractedText =
		data.state === ToothState.ToBeExtracted &&
		view === ToothView.INCISAL &&
		activeTooth &&
		isToothDetailView;

	const showDentalInfo = showDental || isDetail;
	const showPeriodontalInfo = showPeriodontal || isDetail;
	const showPeriodontalIcons =
		showPeriodontalInfo && view !== ToothView.INCISAL;

	return (
		<div
			className={joinClasses([
				styles.toothWrapper,
				showExtractedWrapper ? styles.extracted : '',
				showExtractedText ? styles.extractedText : '',
			])}
			style={
				showExtractedText
					? ({
							'--extracted-text': `'${t(
								'patientTreatment.meddiDENT.dentalCross.overviewModal.extraction',
							)}'`,
					  } as React.CSSProperties)
					: undefined
			}>
			{showToothGrid && (
				<DENTToothGrid tooth={tooth} view={view} scale={scale} />
			)}
			{showPeriodontalInfo && view !== ToothView.INCISAL && (
				<DENTToothPeriodontalLine
					tooth={data}
					view={view}
					scale={scale}
					isDetail={isDetail}
					containerSize={isDetail ? null : originalSize}
					toothMap={toothMap}
				/>
			)}
			<svg
				viewBox={`0 0 ${originalSize.w} ${originalSize.h}`}
				width={renderSize.w}
				height={renderSize.h}
				preserveAspectRatio='xMidYMid meet'>
				<image width={originalSize.w} height={originalSize.h} href={image} />
				{showDentalInfo && (
					<>
						<DENTToothVisual
							tooth={tooth}
							view={view}
							width={originalSize.w}
							height={originalSize.h}
							toothViewData={tData}
						/>
						<DENTToothSurfaces data={tooth} toothViewData={tData} />
					</>
				)}
				{showPeriodontalIcons && (
					<DENTToothIcons
						tooth={tooth}
						width={originalSize.w}
						height={originalSize.h}
						view={view}
					/>
				)}
			</svg>
			{quickSelectMode &&
				view === ToothView.BUCCAL &&
				isToothQuickSelected(tooth.id) && (
					<DENTToothQuickSelected tooth={tooth} />
				)}
		</div>
	);
};

export default DENTToothItem;
